import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/solid';
import { ClipLoader } from 'react-spinners';
import { getConfig } from "../config/config";

const ConfirmationPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const transactionId = queryParams.get('id')?.trim();
    const { apiBaseURL } = getConfig();

    const [status, setStatus] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const lastFetchedIdRef = useRef(null); // ✅ evita doble ejecución en StrictMode para el mismo id

    useEffect(() => {
        let alreadyFetched = false;  // (se deja, pero el control real es el ref)

        const fetchConfirmation = async () => {
            if (alreadyFetched) return;
            alreadyFetched = true;

            if (lastFetchedIdRef.current === transactionId) return;
            lastFetchedIdRef.current = transactionId;

            try {
                const res = await fetch(
                    `${apiBaseURL}/api/Validation/confirmation?transactionId=${transactionId}`
                );
                if (!res.ok) throw new Error('Error al obtener la confirmación');
                const json = await res.json();
                setStatus(json.status);
                setInvoices(json.data || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (transactionId) {
            fetchConfirmation();
        } else {
            setLoading(false);
            setError('ID de transacción no proporcionado');
        }
    }, [transactionId, apiBaseURL]);

    const displayStatus =
        status === 'APPROVED' ? 'APROBADO'
            : status === 'PENDING'  ? 'PENDIENTE'
                : status ? 'RECHAZADO'
                    : '';

    const handleReturn = () => navigate('/');

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8">
            <div className="max-w-lg w-full p-12 bg-white rounded-xl shadow-lg">
                <div className="flex flex-col items-center">
                    {loading && (
                        <div className="w-full flex flex-col items-center justify-center text-center space-y-2 mb-8">
                            <ClipLoader color="#111827" size={42} />
                            <p className="text-sm text-gray-600">
                                Consultando el estado de la transacción...
                            </p>
                        </div>
                    )}

                    {status === 'APPROVED' && (
                        <>
                            <CheckCircleIcon className="w-20 h-20 text-green-500" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Transacción Aprobada</h2>
                            <p className="mt-2 text-sm text-gray-600">La transacción ha sido procesada exitosamente.</p>
                        </>
                    )}
                    {status === 'PENDING' && (
                        <>
                            <ExclamationCircleIcon className="w-20 h-20 text-yellow-500" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Transacción Pendiente</h2>
                            <p className="mt-2 text-sm text-gray-600">
                                El pago se encuentra en estado PENDIENTE. Te notificaremos cuando cambie el estado.
                            </p>
                        </>
                    )}
                    {status && status !== 'APPROVED' && status !== 'PENDING' && (
                        <>
                            <XCircleIcon className="w-20 h-20 text-red-500" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Transacción Rechazada</h2>
                        </>
                    )}

                    <ul className="mt-8 w-full text-left space-y-1">
                        <li><strong>ID de Transacción:</strong> {transactionId || '—'}</li>
                        <li><strong>Estado:</strong> {displayStatus || '—'}</li>
                    </ul>

                    {(status === 'APPROVED' || status === 'PENDING') && (
                        <div className="mt-8 w-full">
                            {loading ? (
                                <p>Cargando facturas...</p>
                            ) : error ? (
                                <p className="text-red-500">Error: {error}</p>
                            ) : invoices.length > 0 ? (
                                <table className="min-w-full mt-4 table-auto">
                                    <thead>
                                    <tr>
                                        <th className="px-4 py-2 border-b">Factura Nº</th>
                                        <th className="px-4 py-2 border-b">Monto</th>
                                        <th className="px-4 py-2 border-b">Fecha de Pago</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {invoices.map((inv, i) => (
                                        <tr key={i}>
                                            <td className="px-4 py-2 border-b">{inv.facturaNumero}</td>
                                            <td className="px-4 py-2 border-b">
                                                {inv.facturaValor != null
                                                    ? `$${inv.facturaValor.toLocaleString('es-CO')}`
                                                    : '$0.00'}
                                            </td>
                                            <td className="px-4 py-2 border-b">
                                                {inv.pagoFecha && !isNaN(new Date(inv.pagoFecha).getTime())
                                                    ? new Date(inv.pagoFecha).toLocaleDateString('es-CO', {
                                                        day:   '2-digit',
                                                        month: 'long',
                                                        year:  'numeric'
                                                    })
                                                    : 'Fecha no disponible'}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="mt-4 text-center">No hay facturas para esta transacción.</p>
                            )}
                        </div>
                    )}

                    <div className="mt-8 w-full text-center">
                        {status === 'APPROVED' && (
                            <>
                                <h3 className="text-2xl font-bold text-gray-900">¡Gracias por su pago!</h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    Tu transacción ha sido procesada exitosamente. Si tienes alguna pregunta, no dudes en contactarnos.
                                </p>
                            </>
                        )}
                        {status === 'PENDING' && (
                            <p className="text-sm text-gray-600">
                                Puedes cerrar esta ventana mientras procesamos tu pago.
                            </p>
                        )}
                        {status && status !== 'APPROVED' && status !== 'PENDING' && (
                            <p className="text-2xl font-bold text-gray-900">Lo sentimos, pero tu pago no fue procesado.</p>
                        )}
                    </div>

                    {!loading && (
                        <button
                            onClick={handleReturn}
                            className="mt-6 px-6 py-3 bg-blue-500 text-white text-lg font-semibold rounded-lg hover:bg-blue-600 transition"
                        >
                            Volver al Inicio
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfirmationPage;
